'use client';

import { useEffect, useRef, type CSSProperties, type RefObject } from 'react';
import styles from './LandingHome.module.css';
import { formatClock, niceTitle, prefersReducedMotion, type Meeting, type ScheduleSnapshot } from './landingData';

const DAY_START = 480; // 8:00 AM
const DAY_END = 1260; // 9:00 PM
/** Same campus identity as the scheduling page (`CAMPUS_COLORS` in app/types/colors.ts). */
const CAMPUS_CLASS: Record<string, string> = {
  Busch: styles.campusBusch, 'College Avenue': styles.campusCAve, 'College Ave': styles.campusCAve,
  Livingston: styles.campusLiv, 'Cook/Douglass': styles.campusCD, 'C/D': styles.campusCD,
};
const campusClass = (campus: string) => CAMPUS_CLASS[campus] ?? styles.campusOther;
const campusLabel = (campus: string) => (campus === 'College Avenue' ? 'College Ave' : campus);
const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

const pct = (minutes: number) => ((minutes - DAY_START) / (DAY_END - DAY_START)) * 100;
const blockStyle = (m: Meeting): CSSProperties => ({
  left: `calc(40px + (100% - 40px) * ${m.day / 5} + 3px)`,
  width: 'calc((100% - 40px) / 5 - 6px)',
  top: `${pct(m.start)}%`,
  height: `calc(${((m.end - m.start) / (DAY_END - DAY_START)) * 100}% - 2px)`,
});

type Props = {
  schedule: ScheduleSnapshot;
  /** 0: add classes, 1: every option that fits, 2: who's teaching */
  step: number;
  /** How many tray chips have appeared during step 0. */
  chipsShown: number;
  countRef: RefObject<HTMLElement | null>;
};

/** The Schedule panel of the word scroll: real Fall 2026 sections falling into a week. */
export default function ScheduleStory({ schedule, step, chipsShown, countRef }: Props) {
  const blockRefs = useRef<HTMLDivElement[]>([]);
  const dropped = useRef(false);
  const meetings = schedule.schedule.flatMap((course) => course.meetings.map((m) => ({ m, course })));
  const campuses = [...new Set(meetings.map(({ m }) => campusLabel(m.campus)))];

  useEffect(() => {
    const blocks = blockRefs.current.filter(Boolean);
    if (step >= 1 && !dropped.current) {
      dropped.current = true;
      if (prefersReducedMotion()) return;
      blocks.forEach((el, i) => {
        const fall = el.offsetTop + el.offsetHeight + 60;
        el.animate([
          { transform: `translateY(${-fall}px) rotate(${((i % 3) - 1) * 3}deg)`, opacity: 1, easing: 'cubic-bezier(.55,0,1,.45)' },
          { transform: 'translateY(0) rotate(0)', offset: 0.7, easing: 'cubic-bezier(0,.55,.45,1)' },
          { transform: 'translateY(-10px)', offset: 0.84, easing: 'cubic-bezier(.55,0,1,.45)' },
          { transform: 'translateY(0)' },
        ], { duration: 760, delay: i * 55, fill: 'backwards' });
      });
    } else if (step === 0 && dropped.current) {
      dropped.current = false;
      blocks.forEach((el) => el.getAnimations().forEach((a) => a.cancel()));
    }
  }, [step]);

  const steps = [
    { title: 'Add the classes you need', body: 'Search all of Fall 2026 by course code, title or professor. Nothing is saved until you say so.' },
    { title: 'Get every option that fits', body: `${schedule.sectionsConsidered} open sections across your 5 courses make ${schedule.conflictFreeOptions.toLocaleString()} conflict-free schedules, with travel time between campuses included.` },
    { title: "See who's teaching", body: 'SIRS ratings from real Rutgers course evaluations sit right on the schedule.' },
  ];

  return (
    <div className={styles.sched}>
      <ol className={styles.schedSteps}>
        {steps.map((s, i) => (
          <li key={s.title} className={i === step ? styles.stepOn : undefined} aria-current={i === step ? 'step' : undefined}>
            <h4>{s.title}</h4>
            <p>{s.body}</p>
          </li>
        ))}
      </ol>
      <div className={styles.window} aria-label="A real Fall 2026 schedule assembling itself">
        <div className={styles.windowBar}>
          <i /><i /><i />
          <span className={styles.windowTitle}>Fall 2026 · My schedule</span>
          <span className={`${styles.count} ${step >= 1 ? styles.countOn : ''}`}><b ref={countRef}>{schedule.conflictFreeOptions.toLocaleString()}</b> conflict-free options</span>
        </div>
        <div className={styles.grid}>
          <div className={styles.days}>{DAYS.map((d) => <div key={d}>{d}</div>)}</div>
          <div className={styles.body}>
            {Array.from({ length: 14 }, (_, k) => 8 + k).map((h) => (
              <div key={h} className={styles.hour} style={{ top: `${pct(h * 60)}%` }}>
                {h < 21 && h % 2 === 0 ? <span>{((h + 11) % 12) + 1}{h < 12 ? 'a' : 'p'}</span> : null}
              </div>
            ))}
            {DAYS.map((d, i) => <div key={d} className={styles.col} style={{ left: `calc(40px + (100% - 40px) * ${i / 5})` }} />)}
            {meetings.map(({ m, course }, i) => {
              const rating = course.sirs ?? course.courseSirs;
              return (
                <div
                  key={`${course.code}-${m.day}-${m.start}`}
                  ref={(el) => { if (el) blockRefs.current[i] = el; }}
                  className={`${styles.block} ${campusClass(m.campus)} ${step >= 1 ? styles.blockOn : ''}`}
                  style={blockStyle(m)}
                >
                  <b>{niceTitle(course.title)}</b>
                  <small className={styles.blockCode}>{course.code}{m.type === 'LEC' ? '' : ` · ${m.type.toLowerCase()}`}</small>
                  {m.end - m.start >= 70 ? <small>{formatClock(m.start, true)} · {m.where}</small> : null}
                  {rating && m.type === 'LEC' ? <span className={`${styles.rating} ${step >= 2 ? styles.ratingOn : ''}`}>★ {rating.toFixed(1)}</span> : null}
                </div>
              );
            })}
          </div>
        </div>
        <div className={styles.legend} aria-label="Campus colors">
          {campuses.map((c) => <span key={c} className={campusClass(c)}><i />{c}</span>)}
        </div>
        <div className={`${styles.tray} ${step === 0 ? '' : styles.trayOff}`} aria-hidden={step !== 0}>
          <div className={styles.traySearch}>Search courses, professors, codes<span className={styles.caret} /></div>
          <div className={styles.chips}>
            {schedule.candidates.map((c, i) => (
              <span key={c.code} className={`${styles.chip} ${campusClass(schedule.schedule[i]?.meetings[0]?.campus ?? '')} ${i < chipsShown ? styles.chipOn : ''}`}>
                <i />{niceTitle(c.title)} <code>{c.code}</code>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
